import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-[calc(100vh-72px)] grid place-items-center p-6 sm:p-10">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FF7E67]/15 text-[#FF7E67] mb-4">
            <Mail className="w-6 h-6" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24]">Reset your password</h1>
          <p className="mt-2 text-sm text-[#857163]">Enter the email you signed up with. We'll send you a link to pick a new password.</p>
        </div>

        {sent ? (
          <div className="cp-card p-6 text-center" data-testid="forgot-password-sent">
            <CheckCircle2 className="w-10 h-10 text-[#8EAC50] mx-auto mb-3" />
            <div className="font-semibold text-[#3B2C24] text-lg">Check your inbox</div>
            <p className="mt-2 text-sm text-[#857163]">
              If an account exists for <b>{email}</b>, we've sent a password-reset link that's good for 60 minutes. Don't forget to check your spam folder.
            </p>
            <Link to="/login" className="mt-6 inline-block text-[#FF7E67] font-semibold hover:underline text-sm" data-testid="back-to-login">
              &larr; Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="cp-card p-6 space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-[#3B2C24]">Email</span>
              <input
                type="email"
                className="cp-input mt-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="forgot-password-email"
              />
            </label>
            {err && <div className="text-sm text-red-600" data-testid="forgot-password-error">{err}</div>}
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="cp-btn-primary w-full disabled:opacity-70"
              data-testid="forgot-password-submit"
            >
              {busy ? "Sending link…" : "Send reset link"}
            </button>
            <div className="text-center">
              <Link to="/login" className="text-xs text-[#857163] hover:text-[#3B2C24]" data-testid="back-to-login">
                &larr; Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
